import React, { useState, lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, ChevronDownIcon, ArrowRightIcon, XCircleIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
const faqs = [{
  question: 'Are there long-term contracts?',
  answer: 'No. All our packages are month-to-month. We believe in earning your business every month, not locking you in. You can cancel anytime with 30 days notice.'
}, {
  question: 'Is the ad budget included in the monthly fee?',
  answer: "No, your ad spend is separate from our management fee. This gives you full control over your budget. We'll recommend a starting budget based on your market and goals."
}, {
  question: 'How often do I get reports?',
  answer: 'All clients receive monthly performance reports. Full Automation Suite clients also get monthly strategy calls to review results and plan next steps.'
}, {
  question: 'What kind of support do I get?',
  answer: 'All clients get email and chat support during business hours. Never Miss A Lead and Full Automation Suite clients get priority response times.'
}, {
  question: 'How quickly can I get started?',
  answer: 'Most clients are fully set up within 5-7 business days. We handle all the technical setup so you can focus on running your business.'
}, {
  question: 'Do I need to be tech-savvy?',
  answer: "Not at all. We handle everything technical. You'll get a simple dashboard and mobile app to manage your leads and appointments."
}];
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200 last:border-0" data-id="element-1896">
      <button className="w-full py-6 flex items-center justify-between text-left focus:outline-none" onClick={() => setIsOpen(!isOpen)} data-id="element-1897">
        <span className="text-lg font-semibold text-gray-900" data-id="element-1898">{question}</span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} className="flex-shrink-0 ml-4 text-viridia" data-id="element-1899">
          <ChevronDownIcon className="w-5 h-5" data-id="element-1900" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-1901">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-1902">
            <p className="pb-6 text-gray-600 leading-relaxed" data-id="element-1903">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function PricingPage() {
  return <div className="bg-viridia-black min-h-screen pt-24" data-id="element-1904">
      <SEOHead title="Pricing | Marketing Packages for Local Businesses | Viridia Analytics Phoenix" description="Transparent, month-to-month pricing for ad campaign management, AI lead conversion, and web design. No long-term contracts. Packages starting for Phoenix local service businesses." canonical="/pricing" keywords="marketing agency pricing, ad management pricing Phoenix, marketing automation cost, web design pricing, local business marketing packages" faq={faqs} data-id="element-1905" />
      {/* HERO SECTION */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center" data-id="element-1906">
        <ScrollReveal data-id="element-1907">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-1908">
            CRM & AUTOMATION
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight" data-id="element-1909">
            Systems That Book Real Jobs -{' '}
            <span className="text-viridia" data-id="element-1910">Pick the One That Fits</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-1911">
            Month-to-month systems that capture leads, automate follow-up, and
            keep your business organized - so you can focus on the work.
          </p>
        </ScrollReveal>
      </section>

      {/* PRICING CARDS SECTION */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1912">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center" data-id="element-1913">
          {/* Card 1: Business Essentials */}
          <ScrollReveal delay={0.1} data-id="element-1914">
            <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:shadow-2xl hover:shadow-viridia/5 transition-all duration-300 h-full flex flex-col" data-id="element-1915">
              <div className="mb-8" data-id="element-1916">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1917">
                  Business Essentials
                </h3>
                <p className="text-gray-400 text-sm h-10" data-id="element-1918">
                  Best for: Businesses that need a CRM foundation, scheduling,
                  and basic lead capture.
                </p>
              </div>
              <div className="mb-8" data-id="element-1919">
                <span className="text-5xl font-extrabold text-white" data-id="element-1920">$497</span>
                <span className="text-gray-500 font-medium" data-id="element-1921">/month</span>
              </div>
              <ul className="space-y-4 mb-8 flex-grow" data-id="element-1922">
                {['CRM Setup & Contact Management', 'Online Booking / Calendar Integration', 'Professional Branded Invoicing & Revenue Tracking (PayPal / Square / Stripe sync)', 'QuickBooks & Local Billing Integration', 'Lead Capture Workflows', 'Lead Platform Auto-Responder (1 auto-response per new lead)', 'GHL Mobile App Access'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-1923">
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-1924" />
                    <span className="text-gray-300 text-sm" data-id="element-1925">{feature}</span>
                  </li>)}
              </ul>
              <div className="mb-6 p-4 bg-viridia-black rounded-lg border border-gray-800" data-id="element-1926">
                <p className="text-sm text-gray-400 mb-2" data-id="element-1927">
                  TFP Landscaping runs on this package - 284% more leads, 28
                  AI-booked estimates in 30 days.
                </p>
                <Link to="/case-studies/tfp-landscaping" className="text-viridia text-sm font-semibold hover:text-viridia-dark flex items-center" data-id="element-1928">
                  See the proof <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-1929" />
                </Link>
              </div>
              <Link to="/contact" className="w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 border border-gray-600 text-white hover:bg-gray-800" data-id="element-1930">
                Get Started
              </Link>
            </div>
          </ScrollReveal>

          {/* Card 2: Never Miss A Lead (MOST POPULAR) */}
          <ScrollReveal delay={0.2} data-id="element-1931">
            <div className="bg-viridia-surface rounded-2xl p-8 border-2 border-viridia relative transform lg:scale-105 shadow-2xl shadow-viridia/10 h-full flex flex-col z-10" data-id="element-1932">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-viridia text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider" data-id="element-1933">
                Most Popular
              </div>
              <div className="mb-8 mt-2" data-id="element-1934">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1935">
                  Never Miss A Lead
                </h3>
                <p className="text-gray-400 text-sm h-10" data-id="element-1936">
                  Best for: Busy contractors who lose jobs when calls go
                  unanswered.
                </p>
              </div>
              <div className="mb-8" data-id="element-1937">
                <span className="text-5xl font-extrabold text-white" data-id="element-1938">$897</span>
                <span className="text-gray-500 font-medium" data-id="element-1939">/month</span>
              </div>
              <ul className="space-y-4 mb-8 flex-grow" data-id="element-1940">
                <li className="flex items-start" data-id="element-1941">
                  <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-1942" />
                  <span className="text-white font-medium text-sm" data-id="element-1943">
                    Everything in Business Essentials
                  </span>
                </li>
                {['AI Voice Receptionist (24/7 call answering)', 'Missed Call Text-Back Automation', 'A2P Registration & Business Number Setup', 'Lead Platform AI Conversational Chatbot', 'Unified Communications Hub'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-1944">
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-1945" />
                    <span className="text-gray-300 text-sm" data-id="element-1946">{feature}</span>
                  </li>)}
              </ul>
              <div className="mb-6 p-4 bg-viridia-black rounded-lg border border-gray-800" data-id="element-1947">
                <p className="text-sm text-gray-400 mb-2" data-id="element-1948">
                  Finish Coat Painting used this package to book 29 AI estimates
                  and close $15.05k in 30 days.
                </p>
                <Link to="/case-studies/finish-coat-painting" className="text-viridia text-sm font-semibold hover:text-viridia-dark flex items-center" data-id="element-1949">
                  See the proof <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-1950" />
                </Link>
              </div>
              <Link to="/contact" className="w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 bg-viridia hover:bg-viridia-dark text-white shadow-lg shadow-viridia/25" data-id="element-1951">
                Get Started
              </Link>
            </div>
          </ScrollReveal>

          {/* Card 3: Full Automation Suite */}
          <ScrollReveal delay={0.3} data-id="element-1952">
            <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:shadow-2xl hover:shadow-viridia/5 transition-all duration-300 h-full flex flex-col" data-id="element-1953">
              <div className="mb-8" data-id="element-1954">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1955">
                  Full Automation Suite
                </h3>
                <p className="text-gray-400 text-sm h-10" data-id="element-1956">
                  Best for: Businesses ready for the complete system. Your
                  business runs on autopilot.
                </p>
              </div>
              <div className="mb-8" data-id="element-1957">
                <span className="text-5xl font-extrabold text-white" data-id="element-1958">
                  $1,497
                </span>
                <span className="text-gray-500 font-medium" data-id="element-1959">/month</span>
              </div>
              <ul className="space-y-4 mb-8 flex-grow" data-id="element-1960">
                <li className="flex items-start" data-id="element-1961">
                  <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-1962" />
                  <span className="text-white font-medium text-sm" data-id="element-1963">
                    Everything in Never Miss A Lead
                  </span>
                </li>
                {['AI Conversational Chatbot (all applicable platforms & website)', 'Reputation Management', 'Review Automation', 'Pipeline Automation', 'Monthly Strategy Calls'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-1964">
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-1965" />
                    <span className="text-gray-300 text-sm" data-id="element-1966">{feature}</span>
                  </li>)}
              </ul>
              <div className="mb-6 p-4 bg-viridia-black rounded-lg border border-gray-800" data-id="element-1967">
                <p className="text-sm text-gray-400" data-id="element-1968">
                  Includes everything that powered both case study results, plus
                  reputation management and monthly strategy calls.
                </p>
              </div>
              <Link to="/contact" className="w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 border border-gray-600 text-white hover:bg-gray-800" data-id="element-1969">
                Get Started
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* AD MANAGEMENT ADD-ON */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1970">
        <ScrollReveal direction="up" data-id="element-1971">
          <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-1972">
            <div data-id="element-1973">
              <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1974">
                Need Ad Management Too?
              </h3>
              <p className="text-gray-400 max-w-2xl" data-id="element-1975">
                Ad campaign management can be added to any package. Pricing
                depends on platforms, ad spend, and scope. Book a call to get a
                custom quote.
              </p>
            </div>
            <Link to="/contact" className="inline-flex items-center text-viridia hover:text-viridia-dark font-semibold transition-colors whitespace-nowrap" data-id="element-1976">
              Get a Custom Ad Quote <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1977" />
            </Link>
          </div>
        </ScrollReveal>
      </section>

      {/* WEBSITE PRICING SECTION */}
      <section id="web-design" className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-1978">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1979">
          <ScrollReveal data-id="element-1980">
            <div className="text-center mb-16" data-id="element-1981">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-1982">
                WEB DESIGN & SEO
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4" data-id="element-1983">
                Website Packages
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-1984">
                High-converting, mobile-first websites built for local service
                businesses. One-time investment.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center" data-id="element-1985">
            {/* Starter Site */}
            <ScrollReveal delay={0.1} data-id="element-1986">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:shadow-2xl hover:shadow-viridia/5 transition-all duration-300 h-full flex flex-col" data-id="element-1987">
                <div className="mb-8" data-id="element-1988">
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1989">
                    Starter Site
                  </h3>
                  <p className="text-gray-400 text-sm h-10" data-id="element-1990">
                    The essentials to get your business online and looking
                    professional.
                  </p>
                </div>
                <div className="mb-8" data-id="element-1991">
                  <span className="text-4xl font-extrabold text-white" data-id="element-1992">
                    $1,200–$1,800
                  </span>
                  <span className="text-gray-500 font-medium ml-2" data-id="element-1993">
                    one-time
                  </span>
                </div>
                <ul className="space-y-4 mb-8 flex-grow" data-id="element-1994">
                  {['3 pages (Home, Services, Contact)', 'Mobile responsive', 'Contact form', 'On-page SEO', 'Google Analytics setup'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-1995">
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-1996" />
                      <span className="text-gray-300 text-sm" data-id="element-1997">{feature}</span>
                    </li>)}
                </ul>
                <div className="mt-auto" data-id="element-1998">
                  <Link to="/contact" className="block w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 border border-gray-600 text-white hover:bg-gray-800 mb-3" data-id="element-1999">
                    Get a Quote
                  </Link>
                  <p className="text-center text-gray-500 text-xs" data-id="element-2000">
                    One-time fee. No hidden costs.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            {/* Growth Site (MOST POPULAR) */}
            <ScrollReveal delay={0.2} data-id="element-2001">
              <div className="bg-viridia-surface rounded-2xl p-8 border-2 border-viridia relative transform lg:scale-105 shadow-2xl shadow-viridia/10 h-full flex flex-col z-10" data-id="element-2002">
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-viridia text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider" data-id="element-2003">
                  Most Popular
                </div>
                <div className="mb-8 mt-2" data-id="element-2004">
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2005">
                    Growth Site
                  </h3>
                  <p className="text-gray-400 text-sm h-10" data-id="element-2006">
                    Built to convert traffic into leads and dominate local
                    search.
                  </p>
                </div>
                <div className="mb-8" data-id="element-2007">
                  <span className="text-4xl font-extrabold text-white" data-id="element-2008">
                    $2,500–$3,000
                  </span>
                  <span className="text-gray-500 font-medium ml-2" data-id="element-2009">
                    one-time
                  </span>
                </div>
                <ul className="space-y-4 mb-8 flex-grow" data-id="element-2010">
                  {['5–7 pages', 'Custom design', 'Service-specific pages', 'Review/trust section', 'Conversion CTAs', 'On-page SEO baked in'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-2011">
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-2012" />
                      <span className="text-gray-300 text-sm" data-id="element-2013">{feature}</span>
                    </li>)}
                </ul>
                <div className="mt-auto" data-id="element-2014">
                  <Link to="/contact" className="block w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 bg-viridia hover:bg-viridia-dark text-white shadow-lg shadow-viridia/25 mb-3" data-id="element-2015">
                    Get a Quote
                  </Link>
                  <p className="text-center text-gray-400 text-xs" data-id="element-2016">
                    One-time fee. No hidden costs.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            {/* Premium Site */}
            <ScrollReveal delay={0.3} data-id="element-2017">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:shadow-2xl hover:shadow-viridia/5 transition-all duration-300 h-full flex flex-col" data-id="element-2018">
                <div className="mb-8" data-id="element-2019">
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2020">
                    Premium Site
                  </h3>
                  <p className="text-gray-400 text-sm h-10" data-id="element-2021">
                    The ultimate digital storefront for established businesses.
                  </p>
                </div>
                <div className="mb-8" data-id="element-2022">
                  <span className="text-4xl font-extrabold text-white" data-id="element-2023">
                    $4,000–$6,000
                  </span>
                  <span className="text-gray-500 font-medium ml-2" data-id="element-2024">
                    one-time
                  </span>
                </div>
                <ul className="space-y-4 mb-8 flex-grow" data-id="element-2025">
                  {['8–12 pages', 'Full custom design', 'Blog setup', 'Multiple service pages', 'Schema markup', 'Lead capture', 'Speed optimization'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-2026">
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-2027" />
                      <span className="text-gray-300 text-sm" data-id="element-2028">{feature}</span>
                    </li>)}
                </ul>
                <div className="mt-auto" data-id="element-2029">
                  <Link to="/contact" className="block w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 border border-gray-600 text-white hover:bg-gray-800 mb-3" data-id="element-2030">
                    Get a Quote
                  </Link>
                  <p className="text-center text-gray-500 text-xs" data-id="element-2031">
                    One-time fee. No hidden costs.
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* LOCAL SEO ADD-ONS */}
      <section className="py-16 bg-viridia-black" data-id="element-2032">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2033">
          <ScrollReveal data-id="element-2034">
            <div className="text-center mb-12" data-id="element-2035">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-2036">
                Local SEO Add-Ons
              </h3>
              <p className="text-gray-400 max-w-2xl mx-auto" data-id="element-2037">
                Ongoing optimization to ensure your new website actually gets
                found by local customers.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2038">
            {/* Basic */}
            <ScrollReveal delay={0.1} data-id="element-2039">
              <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800 h-full flex flex-col" data-id="element-2040">
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-2041">
                  Basic Local SEO
                </h4>
                <div className="mb-6" data-id="element-2042">
                  <span className="text-2xl font-bold text-viridia" data-id="element-2043">
                    $300–$500
                  </span>
                  <span className="text-gray-500 text-sm" data-id="element-2044">/mo</span>
                </div>
                <ul className="space-y-3 mb-6 flex-grow" data-id="element-2045">
                  {['GBP optimization', 'Citations', 'Monthly report'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-2046">
                        <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-2047" />
                        <span className="text-gray-300 text-sm" data-id="element-2048">{feature}</span>
                      </li>)}
                </ul>
              </div>
            </ScrollReveal>

            {/* Standard */}
            <ScrollReveal delay={0.2} data-id="element-2049">
              <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800 h-full flex flex-col" data-id="element-2050">
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-2051">
                  Standard Local SEO
                </h4>
                <div className="mb-6" data-id="element-2052">
                  <span className="text-2xl font-bold text-viridia" data-id="element-2053">
                    $500–$800
                  </span>
                  <span className="text-gray-500 text-sm" data-id="element-2054">/mo</span>
                </div>
                <ul className="space-y-3 mb-6 flex-grow" data-id="element-2055">
                  <li className="flex items-start" data-id="element-2056">
                    <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-2057" />
                    <span className="text-white font-medium text-sm" data-id="element-2058">
                      Everything in Basic
                    </span>
                  </li>
                  {['On-page SEO', 'Schema markup', 'Review management'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-2059">
                        <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-2060" />
                        <span className="text-gray-300 text-sm" data-id="element-2061">{feature}</span>
                      </li>)}
                </ul>
              </div>
            </ScrollReveal>

            {/* Full */}
            <ScrollReveal delay={0.3} data-id="element-2062">
              <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800 h-full flex flex-col" data-id="element-2063">
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-2064">
                  Full Local SEO
                </h4>
                <div className="mb-6" data-id="element-2065">
                  <span className="text-2xl font-bold text-viridia" data-id="element-2066">
                    $800–$1,200
                  </span>
                  <span className="text-gray-500 text-sm" data-id="element-2067">/mo</span>
                </div>
                <ul className="space-y-3 mb-6 flex-grow" data-id="element-2068">
                  <li className="flex items-start" data-id="element-2069">
                    <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-2070" />
                    <span className="text-white font-medium text-sm" data-id="element-2071">
                      Everything in Standard
                    </span>
                  </li>
                  {['Citation building', 'GBP posts & content', 'City pages', 'Competitor tracking'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-2072">
                      <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-2073" />
                      <span className="text-gray-300 text-sm" data-id="element-2074">{feature}</span>
                    </li>)}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ONE-TIME LOCAL PRESENCE SERVICES */}
      <section className="py-16 bg-viridia-black border-t border-gray-800" data-id="element-2075">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2076">
          <ScrollReveal data-id="element-2077">
            <div className="text-center mb-12" data-id="element-2078">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-2079">
                ONE-TIME SERVICE
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-2080">
                Google Business Profile Launch & Verification
              </h3>
              <p className="text-gray-400 max-w-2xl mx-auto" data-id="element-2081">
                If your Google Business Profile isn't set up or verified
                correctly, you may not be showing up where local customers
                search. We handle the setup and verification process so your
                business is properly listed, visible, and ready to support
                reviews, calls, and local trust.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1} data-id="element-2082">
            <div className="bg-viridia-surface rounded-2xl p-8 md:p-10 border border-gray-800" data-id="element-2083">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8" data-id="element-2084">
                <div data-id="element-2085">
                  <h4 className="text-2xl font-bold text-white mb-1" data-id="element-2086">
                    GBP Launch & Verification
                  </h4>
                  <p className="text-gray-400 text-sm" data-id="element-2087">
                    A strong first step for businesses not yet properly set up
                    on Google. Pairs well with our automation, ad, and website
                    services.
                  </p>
                </div>
                <div className="flex-shrink-0 text-right" data-id="element-2088">
                  <span className="text-4xl font-extrabold text-white" data-id="element-2089">
                    $397
                  </span>
                  <span className="text-gray-500 font-medium ml-2" data-id="element-2090">
                    one-time
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-2091">
                {['Create or claim your Google Business Profile', 'Guide or manage the verification process', 'Add correct business name, address & info', 'Set service area & local coverage correctly', 'Choose the right business categories', 'Add business description', 'Add website, phone, hours & core details', 'Upload logo & branded image placeholders (if assets available)', 'Final check that profile is live & properly configured'].map((feature, i) => <div key={i} className="flex items-start" data-id="element-2092">
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-2093" />
                    <span className="text-gray-300 text-sm" data-id="element-2094">{feature}</span>
                  </div>)}
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-4 pt-6 border-t border-gray-800" data-id="element-2095">
                <Link to="/contact" className="inline-flex items-center justify-center px-6 py-3 text-sm font-bold text-viridia-black bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 w-full sm:w-auto" data-id="element-2096">
                  Get Started
                  <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-2097" />
                </Link>
                <p className="text-gray-500 text-xs text-center sm:text-left" data-id="element-2098">
                  This is Viridia's service fee for setup and verification help.
                  Google does not charge for creating a Business Profile.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* HUBSPOT SEO CERTIFICATION */}
      <section className="py-16 bg-viridia-black" data-id="element-2099">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2100">
          <ScrollReveal data-id="element-2101">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center gap-8 md:gap-12" data-id="element-2102">
              <div className="flex-shrink-0 w-full md:w-auto flex justify-center" data-id="element-2103">
                <div className="relative group" data-id="element-2104">
                  <div className="absolute -inset-1 bg-viridia/20 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-2105"></div>
                  <img src="https://cdn.magicpatterns.com/uploads/it67T9N3RYnBZ32NVuR62H/SEOcertified.png" alt="HubSpot Academy SEO Certification - Kevin Rogers" loading="lazy" className="relative w-64 md:w-72 rounded-lg shadow-xl border border-gray-700" data-id="element-2106" />
                </div>
              </div>
              <div className="text-center md:text-left" data-id="element-2107">
                <div className="flex items-center justify-center md:justify-start gap-3 mb-4" data-id="element-2108">
                  <svg viewBox="0 0 512 512" className="w-8 h-8" aria-label="HubSpot logo" data-id="element-2109">
                    <path fill="#FF7A59" d="M380.48 256c0-19.2-9.6-36.48-24.96-47.04V161.6c0-7.68-6.24-13.92-13.92-13.92h-.96c-7.68 0-13.92 6.24-13.92 13.92v40.32c-5.28-1.92-10.88-3.2-16.64-3.52V136c0-17.28-14.08-31.36-31.36-31.36-17.28 0-31.36 14.08-31.36 31.36v62.4c-5.76.32-11.36 1.6-16.64 3.52V161.6c0-7.68-6.24-13.92-13.92-13.92h-.96c-7.68 0-13.92 6.24-13.92 13.92v47.36C186.72 219.52 177.12 236.8 177.12 256c0 29.44 19.52 54.4 46.24 62.72l-24.64 24.64c-3.84 3.84-3.84 10.08 0 13.92 1.92 1.92 4.48 2.88 6.96 2.88s5.04-.96 6.96-2.88l31.04-31.04c5.28 1.6 10.88 2.56 16.64 2.88v44.48c0 17.28 14.08 31.36 31.36 31.36 17.28 0 31.36-14.08 31.36-31.36v-44.48c5.76-.32 11.36-1.28 16.64-2.88l31.04 31.04c1.92 1.92 4.48 2.88 6.96 2.88s5.04-.96 6.96-2.88c3.84-3.84 3.84-10.08 0-13.92l-24.64-24.64C360.96 310.4 380.48 285.44 380.48 256zM278.72 296.96c-22.56 0-40.96-18.4-40.96-40.96s18.4-40.96 40.96-40.96 40.96 18.4 40.96 40.96-18.4 40.96-40.96 40.96z" data-id="element-2110" />
                  </svg>
                  <span className="text-sm font-bold text-[#FF7A59] tracking-wide uppercase" data-id="element-2111">
                    HubSpot Academy
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3" data-id="element-2112">
                  SEO Certified
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-2113">
                  Our team is HubSpot SEO Certified - trained and tested on
                  technical SEO, on-page optimization, link building, and search
                  strategy. Not guesswork. Proven methodology.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FREELANCER COMPARISON */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-2114">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2115">
          <ScrollReveal data-id="element-2116">
            <div className="text-center mb-12" data-id="element-2117">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2118">
                Why Not Just Hire a Freelancer?
              </h2>
              <p className="text-lg text-gray-400" data-id="element-2119">
                You get what you pay for. We build systems that generate
                revenue, not just pretty brochures.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2120">
            <ScrollReveal delay={0.1} data-id="element-2121">
              <div className="bg-viridia-surface/50 rounded-2xl p-8 border border-red-900/30 h-full" data-id="element-2122">
                <h3 className="text-xl font-bold text-gray-300 mb-6 flex items-center" data-id="element-2123">
                  <span className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center mr-3" data-id="element-2124">
                    <XCircleIcon className="w-5 h-5 text-red-400" data-id="element-2125" />
                  </span>
                  The Average Freelancer
                </h3>
                <ul className="space-y-4" data-id="element-2126">
                  {['Cheap, bloated templates', 'No marketing or conversion strategy', 'Zero accountability for results', 'Disappears after launch'].map((item, i) => <li key={i} className="flex items-start" data-id="element-2127">
                      <XCircleIcon className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5 opacity-70" data-id="element-2128" />
                      <span className="text-gray-400" data-id="element-2129">{item}</span>
                    </li>)}
                </ul>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-2130">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-viridia/30 h-full shadow-lg shadow-viridia/5" data-id="element-2131">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center" data-id="element-2132">
                  <span className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center mr-3" data-id="element-2133">
                    <CheckCircleIcon className="w-5 h-5 text-viridia" data-id="element-2134" />
                  </span>
                  Viridia Analytics
                </h3>
                <ul className="space-y-4" data-id="element-2135">
                  {['Conversion-focused, custom builds', 'Deep local SEO knowledge', 'Ongoing support and optimization', 'Built specifically for your market'].map((item, i) => <li key={i} className="flex items-start" data-id="element-2136">
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-2137" />
                      <span className="text-gray-200 font-medium" data-id="element-2138">{item}</span>
                    </li>)}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-light text-gray-900" data-id="element-2139">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2140">
          <ScrollReveal data-id="element-2141">
            <div className="text-center mb-16" data-id="element-2142">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2143">
                Frequently Asked Questions
              </h2>
              <p className="text-gray-600" data-id="element-2144">
                Everything you need to know about our packages and billing.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2} data-id="element-2145">
            <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-6 md:p-8 border border-gray-100" data-id="element-2146">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-2147" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia to-viridia-dark relative overflow-hidden" data-id="element-2148">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-2149"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-2150">
          <ScrollReveal data-id="element-2151">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-2152">
              Not Sure Which Package Is Right?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-2153">
              Book a free call and we'll recommend the perfect plan based on
              your business size and goals.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-lg bg-white text-viridia-dark hover:bg-gray-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1" data-id="element-2154">
              Book Your Free Consultation
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}